import React, { useEffect, useState } from "react";
import { fireStore } from "../../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import "react-datepicker/dist/react-datepicker.css";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const Plannings = () => {
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 1);

  const [startDate, setStartDate] = useState(threeDaysAgo.getTime()); // Store as milliseconds
  const [endDate, setEndDate] = useState(Date.now()); // Store as milliseconds

  const [plannings, setplannings] = useState([]);
  const [filteredPlannings, setFilteredPlannings] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTerritory, setSelectedTerritory] = useState("");

  // Fetch Plannings...
  useEffect(() => {
    const fetchPlannings = async () => {
      try {
        const collectionRef = collection(fireStore, "Plannings");
        const q = query(
          collectionRef,
          where("id", ">=", startDate),
          where("id", "<=", endDate)
        );

        const querySnapshot = await getDocs(q);
        const planningsList = querySnapshot.docs.map((doc) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });

        const sortedPlanningList = planningsList.sort((a, b) => b.id - a.id);
        setplannings(sortedPlanningList);
        setFilteredPlannings(sortedPlanningList);
        // console.log(planningsList);
      } catch (error) {
        console.error("Error fetching plannings:", error);
      }
    };

    fetchPlannings();
  }, [startDate, endDate]);

  // Fetch Territories...
  useEffect(() => {
    const fetchTerritories = async () => {
      try {
        const collectionRef = collection(fireStore, "Territories");
        const querySnapshot = await getDocs(collectionRef);
        const territoriesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort territories in ascending order based on a property, e.g., 'name'
        const sortedTerritories = territoriesList.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });

        setTerritories(sortedTerritories);
      } catch (error) {
        console.error("Error fetching territories:", error);
      }
    };

    fetchTerritories();
  }, []);

  // Export PDF...
  const exportToPDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 0;

    for (let i = 0; i < filteredPlannings.length; i++) {
      const PlanningElement = document.getElementById(
        `Planning-${filteredPlannings[i].id}`
      );

      if (PlanningElement) {
        const canvas = await html2canvas(PlanningElement, { scale: 1 });
        const imgData = canvas.toDataURL("image/jpeg", 0.7);
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (yPosition + imgHeight > pageHeight) {
          doc.addPage();
          yPosition = 0;
        }

        doc.addImage(imgData, "PNG", 0, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight;
      }
    }

    doc.save("Plannings.pdf");
  };

  // Territory Filters
  useEffect(() => {
    const filtered = plannings.filter(
      (visit) =>
        visit.user?.territory.name.toLowerCase() ===
        selectedTerritory.toLocaleLowerCase()
    );

    setFilteredPlannings(filtered);
    if (selectedTerritory === "All") {
      setFilteredPlannings(plannings);
    }
  }, [selectedTerritory]);

  useEffect(() => {
    if (!searchQuery.trim() || searchQuery === "All") {
      setFilteredPlannings(plannings); // Reset list when empty or "All"
    } else {
      const filtered = plannings.filter((visit) =>
        visit.user?.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredPlannings(filtered);
    }
  }, [searchQuery, plannings]); // Now listens to `plannings` too!

  return (
    <div className="p-4 bg-lightgray h-full overflow-y-scroll">
      <div className="bg-white px-4 py-2 rounded-lg shadow-lg max-w-5xl mx-auto mt-2 lg:sticky top-0 z-50">
        <div className="flex flex-wrap justify-between items-center py-2">
          <h1 className="text-lg font-semibold text-liquidblue">
            Monthly Review and Planning Report
          </h1>

          <button
            onClick={exportToPDF}
            className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-3 py-1"
          >
            PDF
          </button>
        </div>
        <div className="flex flex-wrap justify-between items-center mb-2 space-y-2 md:space-y-0">
          <select
            id="territories"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            onChange={(e) => setSelectedTerritory(e.target.value)}
            value={selectedTerritory}
          >
            <option value="All">Select Territory</option>
            {territories.map((territory) => (
              <option key={territory.id} value={territory.name}>
                {territory.name}
              </option>
            ))}
          </select>

          <div className="flex items-center">
            {/* From Date Picker */}
            <label
              htmlFor="from-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              From Date
            </label>
            <input
              type="date"
              id="from-date"
              className="flex-grow border p-2 rounded focus:ring focus:ring-blue-300"
              value={new Date(startDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
              onChange={(e) => setStartDate(new Date(e.target.value).getTime())} // Convert selected date to timestamp
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-between items-center mb-4 space-y-2 md:space-y-0">
          <input
            type="text"
            placeholder="Search"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="flex items-center ">
            <label
              htmlFor="from-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              From Date
            </label>
            <input
              type="date"
              id="from-date"
              className="flex-grow border p-2 rounded focus:ring focus:ring-blue-300"
              value={new Date(endDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
              onChange={(e) => setEndDate(new Date(e.target.value).getTime())} // Convert selected date to timestamp
            />
          </div>
        </div>
      </div>
      {filteredPlannings.map((item) => (
        <div
          id={`Planning-${item.id}`}
          key={item.id}
          className="bg-white p-6 rounded-lg shadow-lg max-w-5xl mx-auto mt-4"
        >
          <h1 className="text-xl font-semibold py-4 text-liquidblue">
            {item.user?.territory?.name}
          </h1>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="flex justify-around">
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.user?.name}
                  </th>
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.user?.phone}
                  </th>

                  <th className="py-2 px-4  text-left">
                    {new Date(Number(item.id)).toLocaleDateString("en-GB")}{" "}
                    {new Date(Number(item.id)).toLocaleTimeString("en-GB", {
                      hour12: true,
                    })}{" "}
                  </th>
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.user?.territory?.name ? (
                      <h1 className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-4 py-1">
                        {item.user.territory.name}
                      </h1>
                    ) : null}
                  </th>
                </tr>
              </thead>
              <tbody className="p-2">
                <tr
                  className="p-2"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    background: "teal",
                  }}
                >
                  <th className="text-white">Activities</th>
                  <th className="text-white">
                    {item.reviewMonth}(Review Month)
                  </th>
                  <th className="text-white">{item.planMonth}(Plan Month)</th>
                </tr>

                <tr
                  className="bg-teal-400 text-teal-900 p-2"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <th className="flex-1"></th>
                  <th className="text-center">Target</th>
                  <th className="ml-20 text-center">Achievement</th>
                  <th className="flex-1 text-end">Planning</th>
                </tr>

                <tr
                  className="bg-teal-200 p-1"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <th className="flex-1 text-start">FMFD</th>
                  <td className="text-center">{item.fmTarget}</td>
                  <td className="ml-20 text-center">{item.fmAchievement}</td>
                  <td className="flex-1 text-end">{item.fmfdPlanning}</td>
                </tr>
                <tr
                  className="bg-teal-200 p-1"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <th className="flex-1 text-start">DEMOS</th>
                  <td className="text-center">{item.demoTarget}</td>
                  <td className="ml-20 text-center">{item.demoAchievement}</td>
                  <td className="flex-1 text-end">{item.demosPlanning}</td>
                </tr>

                <tr
                  className="bg-teal-200 p-1"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <th className="flex-1 text-start">SALES</th>
                  <td className="text-center">{item.salesTarget}</td>
                  <td className="ml-20 text-start">{item.salesAchievement}</td>
                  <td className="flex-1 text-end">{item.salesPlanning}</td>
                </tr>
                <tr
                  className="bg-teal-200 p-1"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <th className="flex-1 text-start">COLLECTIONS</th>
                  <td className="text-center">{item.collectionTarget}</td>
                  <td className="ml-20 text-center">
                    {item.collectionAchievement}
                  </td>
                  <td className="flex-1 text-end">
                    {item.collectionsPlanning}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Plannings;
